import React from 'react';

import { useStoreContext } from 'features/store';
import { TWELVE_HOUR_TIME_PARSE_FORMAT } from 'features/store/constants';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import {
  checkIfDateIsWithinCloseTimeAndMinutes,
  getStoreLocalTime,
  readableCloseHourToday,
} from 'features/store/utils';
import { useIntl } from 'react-intl';

import { IServerOrder } from '@rbi-ctg/menu';
import QRCode from 'components/qrcode';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import { useConfigValue } from 'hooks/configs/use-config-value';
import useDialogModal from 'hooks/use-dialog-modal';
import useOrderNumber from 'hooks/use-order-number';
import { PickupPin, QRContainer, QRWrapper } from 'pages/order-confirmation/common';
import OrderIssues from 'pages/order-confirmation/order-issues';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OrderInjectionFlowVariations } from 'state/launchdarkly/variations';
import { ServiceMode } from 'state/order';
import { usePickupTime } from 'state/order/hooks/use-pickup-time';
import { primitive } from 'styles/constants/primitives';
import { fullBrandName } from 'utils/environment';

import { MapCard } from '../components/map-card';
import { OrderConfirmationHeader } from '../components/order-confirmation-header';
import { OrderDetailsCard } from '../components/order-details-card';
import { ServiceModeHeading } from '../components/service-mode-heading';
import { Container, Section, SectionTitle } from '../styled';

const qrCodeOptions = {
  margin: 1,
  scale: 4,
  color: {
    dark: primitive.$black,
    light: primitive.$white,
  },
};

function PickupConfirmed({ serverOrder }: { serverOrder: IServerOrder }) {
  const timeFormatConfig = useConfigValue({
    key: 'timeFormat',
    defaultValue: TWELVE_HOUR_TIME_PARSE_FORMAT,
  });
  const { formatMessage } = useIntl();
  const { store } = useStoreContext();
  const isShowingOrderingIssue = useFlag(LaunchDarklyFlag.SHOW_ORDERING_ISSUE);
  const enableClosingTimeDialog = useFlag(LaunchDarklyFlag.ENABLE_CLOSING_TIME_DIALOG); // TODO: Remove
  const { isOpenByServiceMode } = useStoreAvailabilityFns();

  const serverOrderServiceMode = serverOrder.cart.serviceMode;

  const isPickupServiceMode =
    serverOrderServiceMode === ServiceMode.TAKEOUT ||
    serverOrderServiceMode === ServiceMode.DRIVE_THRU;

  const orderInjectFlow = useFlag(LaunchDarklyFlag.ORDER_INJECTION_FLOW);
  const showProgress = orderInjectFlow === OrderInjectionFlowVariations.IM_HERE;

  const storeLocalTime = getStoreLocalTime();

  const showPickupClosingTimeDialog =
    enableClosingTimeDialog &&
    isPickupServiceMode &&
    (isOpenByServiceMode(store, ServiceMode.DRIVE_THRU, storeLocalTime) ||
      isOpenByServiceMode(store, ServiceMode.EAT_IN, storeLocalTime));
  !!store.driveThruHours &&
    checkIfDateIsWithinCloseTimeAndMinutes(
      store.driveThruHours,
      storeLocalTime ?? '',
      store.timezone ?? '',
      60
    );

  const [PickupClosingTimeDialog] = useDialogModal({
    modalAppearanceEventMessage: 'Store closing hour dialog',
    init: showPickupClosingTimeDialog,
  });

  const orderNumber = useOrderNumber(serverOrder);

  const { fulfillmentDetails } = serverOrder;
  const { pickupDate } = usePickupTime({ serverOrder });

  const pickupOrderAlert = formatMessage(
    { id: 'pickUpOrderSuccessfulAlert' },
    {
      orderNumber,
      brand: fullBrandName(),
      address: `${serverOrder.cart.storeAddress.addressLine1} ${serverOrder.cart.storeAddress.addressLine2}`,
    }
  );
  return (
    <>
      <Container>
        <Section>
          <OrderConfirmationHeader showProgress={showProgress} serverOrder={serverOrder} />
        </Section>

        <Section>
          <ServiceModeHeading serviceMode={serverOrder.cart.serviceMode} />
          <MapCard
            storeAddress={serverOrder.cart.storeAddress}
            storeDetails={serverOrder.cart.storeDetails}
            orderDate={pickupDate}
            serverOrderServiceMode={serverOrder.cart.serviceMode}
          />
        </Section>

        <Section>
          <SectionTitle focusable accessible accessibilityRole="header">
            {formatMessage({ id: 'order' })}
          </SectionTitle>
          <OrderDetailsCard order={serverOrder} />
        </Section>

        {!!fulfillmentDetails && (
          <Section>
            {!!fulfillmentDetails?.pickupQrCode && (
              <QRContainer>
                <QRWrapper>
                  <QRCode
                    barcode={fulfillmentDetails.pickupQrCode}
                    options={qrCodeOptions}
                    testID="qr-code"
                  />
                </QRWrapper>
              </QRContainer>
            )}

            {!!fulfillmentDetails?.pickupPin && (
              <PickupPin>{fulfillmentDetails.pickupPin}</PickupPin>
            )}
          </Section>
        )}

        {isShowingOrderingIssue && (
          <Section>
            <OrderIssues />
          </Section>
        )}
      </Container>

      <VisuallyHidden role="alert" accessibilityLabel={pickupOrderAlert} />

      <PickupClosingTimeDialog
        testID="store-closing-hour-dialog"
        heading={formatMessage({ id: 'orderPickup' })}
        body={formatMessage(
          { id: 'dontForgetToPickYourOrder' },
          { time: readableCloseHourToday(store, timeFormatConfig) }
        )}
      />
    </>
  );
}

export default React.memo(PickupConfirmed);
